import type { SocialProvider } from "@artha/types";
import { useMutation } from "@tanstack/react-query";

import { apiFetch } from "@/lib/apiClient";
import { queryKeys } from "@/lib/queryKeys";

interface ApiSuccessResponse<T> {
  success: true;
  message: string;
  data: T;
}

interface SocialRedirectPayload {
  authorizationUrl: string;
}

export const fetchSocialRedirect = async (
  payload: { provider: SocialProvider; username?: string; referralToken?: string }
): Promise<SocialRedirectPayload> => {
  const { provider, username, referralToken } = payload;
  const url = new URL(`/auth/social/${provider}/redirect`, window.location.origin);
  if (username) {
    url.searchParams.set("username", username);
  }
  if (referralToken) {
    url.searchParams.set("referralToken", referralToken);
  }

  const { ok, data } = await apiFetch<ApiSuccessResponse<SocialRedirectPayload>>(
    url.pathname + url.search
  );

  if (!ok || !data?.data?.authorizationUrl) {
    throw new Error("Unable to start social authentication flow");
  }

  return data.data;
};

export const useSocialRedirectMutation = () =>
  useMutation<SocialRedirectPayload, Error, { provider: SocialProvider; username?: string; referralToken?: string }>({
    mutationKey: queryKeys.socialRedirect(),
    mutationFn: fetchSocialRedirect,
  });

export const fetchLogout = async (): Promise<void> => {
  const { ok } = await apiFetch("/auth/logout", {
    method: "POST",
  });

  if (!ok) {
    throw new Error("Failed to logout");
  }
};

export const useLogoutMutation = () =>
  useMutation<void, Error, void>({
    mutationKey: queryKeys.logout(),
    mutationFn: fetchLogout,
  });
